import React from 'react';
import { X, Play, Image as ImageIcon } from 'lucide-react';
import { Exercise } from '../types';

interface ExerciseInfoModalProps {
  exercise: Exercise | null;
  isOpen: boolean;
  onClose: () => void;
}

const ExerciseInfoModal: React.FC<ExerciseInfoModalProps> = ({
  exercise,
  isOpen,
  onClose
}) => {
  if (!isOpen || !exercise) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Заголовок */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 pr-4">
            {exercise.name}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Медиа контент */}
          {(exercise.image_url || exercise.video_url) && (
            <div className="space-y-4">
              {/* Видео */}
              {exercise.video_url && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                    <Play size={20} className="mr-2 text-blue-600" />
                    Видео демонстрация
                  </h3>
                  <div className="relative aspect-video bg-gray-100 rounded-lg overflow-hidden">
                    <video
                      controls
                      className="w-full h-full object-cover"
                      poster={exercise.image_url}
                    >
                      <source src={exercise.video_url} type="video/mp4" />
                      <source src={exercise.video_url} type="video/webm" />
                      Ваш браузер не поддерживает видео.
                    </video>
                  </div>
                </div>
              )}

              {/* Изображение (если нет видео) */}
              {exercise.image_url && !exercise.video_url && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                    <ImageIcon size={20} className="mr-2 text-blue-600" />
                    Изображение
                  </h3>
                  <div className="relative aspect-video bg-gray-100 rounded-lg overflow-hidden">
                    <img
                      src={exercise.image_url}
                      alt={exercise.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Описание */}
          {exercise.description && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3">
                Описание
              </h3>
              <p className="text-gray-700 leading-relaxed">
                {exercise.description}
              </p>
            </div>
          )}

          {/* Инструкции */}
          {exercise.instructions && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3">
                Инструкции по выполнению
              </h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                  {exercise.instructions}
                </p>
              </div>
            </div>
          )}

          {/* Группы мышц */}
          {exercise.muscle_groups && exercise.muscle_groups.length > 0 && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3">
                Группы мышц
              </h3>
              <div className="flex flex-wrap gap-2">
                {exercise.muscle_groups.map((group) => (
                  <span
                    key={group.id}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                  >
                    {group.name}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Информация о создателе */}
          <div className="border-t border-gray-200 pt-4">
            <p className="text-sm text-gray-500">
              {exercise.is_public ? 'Публичное упражнение' : 'Приватное упражнение'}
              {exercise.created_by_trainer && (
                <span className="ml-2">
                  • Создано: {exercise.created_by_trainer.first_name} {exercise.created_by_trainer.last_name}
                </span>
              )}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExerciseInfoModal; 